import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { ChevronLeft, ChevronRight, Download, Users, Phone, MessageSquare, Mail, Activity, AlertCircle, TrendingUp, Search, RotateCcw, Filter, Calendar, Building2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import API from "../api/api01";
import { toast } from "sonner";
import { cn } from "../ui/utils";
import { exportToCSV } from "../utils/exportUtils";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../ui/label";

interface UnifiedLead {
  id: string;
  sNo?: number;
  name: string;
  source: string;
  status: string;
  campus: string;
  phone?: string;
  email?: string;
}

export function LeadReports() {
  const [sources, setSources] = useState<string[]>(["All"]);
  const [activeTab, setActiveTab] = useState("All");
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState<UnifiedLead[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [campusFilter, setCampusFilter] = useState("All");
  
  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 100;

  useEffect(() => {
    fetchUnifiedLeads();
  }, []);

  const fetchUnifiedLeads = async () => {
    setLoading(true);
    try {
      // Fetching from standard leads endpoint.
      // Since no dedicated endpoints exist for WhatsApp/SMS/Email,
      // we extract all leads and assume they contain the campaign sources.
      const response = await API.get("/leads/");
      let allLeads: UnifiedLead[] = [];

      if (response.data && Array.isArray(response.data)) {
        response.data.forEach((stage: any) => {
          (stage.leads || []).forEach((lead: any) => {
            // Normalizing data from leads endpoint
            allLeads.push({
              id: lead.id,
              name: lead.name || "Unknown",
              source: lead.source || "Unknown",
              status: stage.name || lead.lead_status || "Unknown",
              campus: lead.campus || "Not Specified",
              phone: lead.phone,
              email: lead.email,
            });
          });
        });
      }

      const uniqueSources = Array.from(new Set(allLeads.map(l => l.source))).filter(s => s && s !== "Unknown");
      setSources(["All", ...uniqueSources]);
      setLeads(allLeads);
    } catch (error) {
      console.error("Failed to fetch campaign leads:", error);
      // The global API interceptor in api01.ts will handle the user-facing toast
      // with the specific error message from the backend.
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchUnifiedLeads();
  };

  const getSourceIcon = (source: string) => {
    const s = source.toLowerCase();
    if (s.includes("auto") || s.includes("dialer")) return <Phone className="h-4 w-4 text-blue-500" />;
    if (s.includes("sms")) return <MessageSquare className="h-4 w-4 text-emerald-500" />;
    if (s.includes("whatsapp")) return <MessageSquare className="h-4 w-4 text-green-600" />;
    if (s.includes("email")) return <Mail className="h-4 w-4 text-rose-500" />;
    if (s.includes("social")) return <Activity className="h-4 w-4 text-purple-500" />;
    return <Activity className="h-4 w-4 text-slate-500" />;
  };

  const filteredLeads = useMemo(() => {
    let filtered = leads;
    if (activeTab !== "All") {
      filtered = leads.filter(l => l.source.toLowerCase() === activeTab.toLowerCase());
    }
    if (statusFilter !== "All") {
      filtered = filtered.filter(l => l.status.toLowerCase().includes(statusFilter.toLowerCase()));
    }
    if (campusFilter !== "All") {
      filtered = filtered.filter(l => l.campus.toLowerCase() === campusFilter.toLowerCase());
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(l => 
        l.name.toLowerCase().includes(q) || 
        l.phone?.toLowerCase().includes(q) ||
        l.email?.toLowerCase().includes(q)
      );
    }
    // Add S.NO
    return filtered.map((l, idx) => ({ ...l, sNo: idx + 1 }));
  }, [leads, activeTab, searchQuery, statusFilter, campusFilter]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter, campusFilter, activeTab]);

  const totalPages = Math.ceil(filteredLeads.length / pageSize) || 1;
  const paginatedLeads = filteredLeads.slice((page - 1) * pageSize, page * pageSize);

  const handleExport = () => {
    if (filteredLeads.length === 0) {
      toast.error("No data to export");
      return;
    }
    const dataToExport = filteredLeads.map(l => ({
      "S.NO": l.sNo,
      "Name": l.name,
      "Source": l.source,
      "Status": l.status,
      "College Name": l.campus
    }));
    exportToCSV(dataToExport, `Campaign_Reports_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`);
    toast.success("Report exported successfully");
  };

  const getSourceConfig = (source: string) => {
    switch (source) {
      case "All":
        return { 
          icon: Users, 
          trend: "+12.5%",
          color: "indigo",
          gradientClass: "from-indigo-500/0 via-indigo-500 to-indigo-500/0",
          bgGradientClass: "from-indigo-500/10",
          iconClass: "bg-indigo-500/10 text-indigo-500",
          activeIconClass: "bg-indigo-500 text-white",
          activeCardClass: "border-indigo-500/50 shadow-lg shadow-indigo-500/10",
          indicatorClass: "bg-indigo-500"
        };
      case "Auto Dialer":
        return { 
          icon: Phone, 
          trend: "+8.2%",
          color: "emerald",
          gradientClass: "from-emerald-500/0 via-emerald-500 to-emerald-500/0",
          bgGradientClass: "from-emerald-500/10",
          iconClass: "bg-emerald-500/10 text-emerald-500",
          activeIconClass: "bg-emerald-500 text-white",
          activeCardClass: "border-emerald-500/50 shadow-lg shadow-emerald-500/10",
          indicatorClass: "bg-emerald-500"
        };
      case "SMS":
        return { 
          icon: MessageSquare, 
          trend: "+15.3%",
          color: "violet",
          gradientClass: "from-violet-500/0 via-violet-500 to-violet-500/0",
          bgGradientClass: "from-violet-500/10",
          iconClass: "bg-violet-500/10 text-violet-500",
          activeIconClass: "bg-violet-500 text-white",
          activeCardClass: "border-violet-500/50 shadow-lg shadow-violet-500/10",
          indicatorClass: "bg-violet-500"
        };
      case "WhatsApp":
        return { 
          icon: MessageSquare, 
          trend: "+5.7%",
          color: "green",
          gradientClass: "from-green-500/0 via-green-500 to-green-500/0",
          bgGradientClass: "from-green-500/10",
          iconClass: "bg-green-500/10 text-green-500",
          activeIconClass: "bg-green-500 text-white",
          activeCardClass: "border-green-500/50 shadow-lg shadow-green-500/10",
          indicatorClass: "bg-green-500"
        };
      case "Email":
        return { 
          icon: Mail, 
          trend: "+3.1%",
          color: "rose",
          gradientClass: "from-rose-500/0 via-rose-500 to-rose-500/0",
          bgGradientClass: "from-rose-500/10",
          iconClass: "bg-rose-500/10 text-rose-500",
          activeIconClass: "bg-rose-500 text-white",
          activeCardClass: "border-rose-500/50 shadow-lg shadow-rose-500/10",
          indicatorClass: "bg-rose-500"
        };
      case "Social Media":
      case "Social":
        return { 
          icon: Activity, 
          trend: "+10.4%",
          color: "fuchsia",
          gradientClass: "from-fuchsia-500/0 via-fuchsia-500 to-fuchsia-500/0",
          bgGradientClass: "from-fuchsia-500/10",
          iconClass: "bg-fuchsia-500/10 text-fuchsia-500",
          activeIconClass: "bg-fuchsia-500 text-white",
          activeCardClass: "border-fuchsia-500/50 shadow-lg shadow-fuchsia-500/10",
          indicatorClass: "bg-fuchsia-500"
        };
      default:
        return { 
          icon: Activity, 
          trend: "+0.0%",
          color: "slate",
          gradientClass: "from-slate-500/0 via-slate-500 to-slate-500/0",
          bgGradientClass: "from-slate-500/10",
          iconClass: "bg-slate-500/10 text-slate-500",
          activeIconClass: "bg-slate-50 text-slate-900",
          activeCardClass: "border-slate-500/50 shadow-lg shadow-slate-500/10",
          indicatorClass: "bg-slate-500"
        };
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6 bg-slate-50 dark:bg-[#020617] min-h-full transition-colors duration-300">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white bg-clip-text">
            Campaign Reports
          </h1>
          <p className="text-muted-foreground mt-1 font-medium">
            Dynamic analytics for Auto Dialer, SMS, WhatsApp, and Social campaigns
          </p>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search leads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
            />
          </div>
          <Button 
            variant="outline" 
            size="icon" 
            className="rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RotateCcw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                className={cn(
                  "rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 font-bold text-xs relative",
                  (statusFilter !== "All" || campusFilter !== "All") && "border-blue-500 text-blue-500"
                )}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filter
                {(statusFilter !== "All" || campusFilter !== "All") && (
                  <span className="absolute -top-1 -right-1 h-3 w-3 bg-blue-500 rounded-full border-2 border-white dark:border-slate-900" />
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4 rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-2xl backdrop-blur-xl">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-sm">Advanced Filters</h4>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 text-[10px] uppercase font-bold"
                    onClick={() => {
                      setStatusFilter("All");
                      setCampusFilter("All");
                    }}
                  >
                    Reset
                  </Button>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase text-muted-foreground">Lead Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      <SelectItem value="All">All Statuses</SelectItem>
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="Hot">Hot</SelectItem>
                      <SelectItem value="Warm">Warm</SelectItem>
                      <SelectItem value="Cold">Cold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase text-muted-foreground">College / University</Label>
                  <Select value={campusFilter} onValueChange={setCampusFilter}>
                    <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800">
                      <SelectValue placeholder="Select Campus" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      <SelectItem value="All">All Institutions</SelectItem>
                      {Array.from(new Set(leads.map(l => l.campus))).map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <Button variant="outline" className="hidden lg:flex rounded-xl bg-white dark:bg-[#0f172a] shadow-sm border-slate-200 dark:border-slate-800" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards Acting as Tabs */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        {sources.map((source, index) => {
          const config = getSourceConfig(source);
          const Icon = config.icon;
          const isActive = activeTab === source;
          const count = leads.filter(l => source === "All" ? true : l.source.toLowerCase() === source.toLowerCase()).length;

          return (
            <motion.div
              key={source}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => { setActiveTab(source); setPage(1); }}
              className={cn(
                "group relative overflow-hidden cursor-pointer transition-all duration-300 rounded-2xl border bg-white dark:bg-slate-900/50 backdrop-blur-xl",
                isActive 
                  ? config.activeCardClass 
                  : "border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700"
              )}
            >
              {/* Premium Glow Effect */}
              {isActive && (
                <div className={cn("absolute inset-0 opacity-20 bg-gradient-to-br", config.bgGradientClass)} />
              )}
              
              <div className="p-5 relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <div className={cn(
                    "h-10 w-10 rounded-xl transition-all duration-300 flex items-center justify-center", 
                    isActive ? config.activeIconClass : config.iconClass,
                    "group-hover:text-slate-400 dark:group-hover:text-slate-500" // Greyish hover on logo
                  )}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-none text-[10px] font-bold">
                    {config.trend}
                  </Badge>
                </div>
                
                <div className="space-y-1">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    {source === "All" ? "Total Leads" : source}
                  </p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-2xl font-black text-slate-900 dark:text-white">
                      {count.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              
              {isActive && (
                <motion.div 
                  layoutId="activeTabIndicator"
                  className={cn("absolute bottom-0 left-0 w-full h-1", config.indicatorClass)}
                  transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                />
              )}
            </motion.div>
          );
        })}
      </div>

      <Card className="shadow-sm bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
        <CardContent className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
              <p className="text-sm font-medium uppercase tracking-widest animate-pulse">Syncing Campaign Data...</p>
            </div>
          ) : paginatedLeads.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
              <AlertCircle className="h-10 w-10 mb-4 opacity-20" />
              <p className="text-lg font-bold text-slate-700 dark:text-slate-300">No Leads Found</p>
              <p className="text-sm mt-1">No data available for {activeTab === "All" ? "any campaign" : `the ${activeTab} campaign`}</p>
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-[#020617] shadow-sm">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-slate-50 dark:bg-slate-900/50">
                    <TableRow className="border-b border-slate-200 dark:border-slate-800">
                      <TableHead className="w-[80px] font-bold">S.NO</TableHead>
                      <TableHead className="font-bold">NAME</TableHead>
                      <TableHead className="font-bold">SOURCE</TableHead>
                      <TableHead className="font-bold">STATUS</TableHead>
                      <TableHead className="font-bold">COLLEGE / UNIVERSITY</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <AnimatePresence mode="popLayout">
                      {paginatedLeads.map((lead) => {
                        const statusColor = 
                          lead.status.toLowerCase().includes("hot") ? "bg-red-500/10 text-red-600 dark:text-red-400" :
                          lead.status.toLowerCase().includes("warm") ? "bg-orange-500/10 text-orange-600 dark:text-orange-400" :
                          lead.status.toLowerCase().includes("cold") ? "bg-blue-500/10 text-blue-600 dark:text-blue-400" :
                          lead.status.toLowerCase().includes("new") ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" :
                          "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400";

                        return (
                          <motion.tr 
                            key={lead.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-100 dark:border-slate-800/50 group"
                          >
                            <TableCell className="font-mono text-[11px] text-muted-foreground/60">{lead.sNo}</TableCell>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-bold text-slate-900 dark:text-slate-100">{lead.name}</span>
                                {lead.phone && <span className="text-[10px] text-muted-foreground font-medium">{lead.phone}</span>}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 group-hover:bg-white dark:group-hover:bg-slate-700 transition-colors">
                                  {getSourceIcon(lead.source)}
                                </div>
                                <span className="text-xs text-slate-700 dark:text-slate-300 font-bold uppercase tracking-tight">{lead.source}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={cn("border-none font-bold text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-lg", statusColor)}>
                                {lead.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                                <Building2 className="h-3 w-3 opacity-50" />
                                {lead.campus}
                              </div>
                            </TableCell>
                          </motion.tr>
                        );
                      })}
                    </AnimatePresence>
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination Controls */}
              <div className="flex items-center justify-between px-6 py-4 bg-slate-50 dark:bg-slate-900/30 border-t border-slate-200 dark:border-slate-800">
                <p className="text-sm text-muted-foreground">
                  Showing <span className="font-medium text-foreground">{paginatedLeads.length}</span> of <span className="font-medium text-foreground">{filteredLeads.length}</span> entries
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-700"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm font-medium px-4">
                    {page} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-700"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
